// A seção Segurança. (#880)

import { type ReactNode } from 'react';
import { Estado } from '@/shared/ui/estados/estados';
import { useSessao } from '@/shared/sessao/contexto';
import { useSegurancaDaConta } from '@/shared/api/consultas';
import { Botao, Cartao } from '@/shared/ui';
import { Campo } from './partes-da-secao';

/**
 * Segurança da conta — o segundo fator (B15).
 *
 * ── Por que aqui, e não só no aviso do topo ───────────────────────────────
 *
 * O aviso é passageiro: ele aparece para quem ainda não cadastrou, dá para
 * dispensar por uma semana, e some de vez quando o segundo fator existe. Se
 * ele fosse o único lugar, quem dispensou não teria como voltar, e quem já
 * cadastrou não teria onde conferir.
 *
 * Uma seção fixa responde as duas: onde cadastrar, e se já está.
 *
 * ── A verdade vem do provedor ─────────────────────────────────────────────
 *
 * E não do `amr` do token, que diz como a pessoa entrou NESTA sessão. Ler dali
 * fazia a tela dizer "não tem" para quem tinha — e alguém cadastrou duas vezes
 * por causa disso, ficando com duas credenciais na conta.
 */
export function SecaoSeguranca(): ReactNode {
  const { cadastrarSegundoFator } = useSessao();
  const consulta = useSegurancaDaConta();

  return (
    <section className="admin__secao" aria-label="Segurança">
      <p className="admin__nota">
        O segundo fator protege o acesso mesmo que a senha vaze. Ele é um código de seis dígitos
        gerado por um aplicativo no seu celular.
      </p>

      <Estado consulta={consulta} forma="ficha" linhas={2}>
        {(seguranca) => (
          <Cartao variante="folgado">
            <dl className="admin__campos ficha">
              <Campo
                rotulo="Segundo fator"
                valor={
                  seguranca.segundoFator === null
                    ? 'não foi possível consultar'
                    : seguranca.segundoFator
                      ? 'cadastrado'
                      : 'não cadastrado'
                }
              />
              <Campo
                rotulo="Exigido pela organização"
                valor={seguranca.exigidoPelaRevenda ? 'sim' : 'não, mas é recomendado'}
              />
            </dl>

            {seguranca.segundoFator === false && (
              <Botao onClick={() => void cadastrarSegundoFator()}>Cadastrar segundo fator</Botao>
            )}

            {seguranca.segundoFator === true && (
              <p className="admin__nota">
                Já está ativo. Para trocar de aparelho, peça a alguém com papel de administração
                para remover o atual e cadastrar de novo aqui em seguida.
              </p>
            )}

            {seguranca.segundoFator === null && (
              <p className="admin__nota">
                Não foi possível verificar agora. Recarregue em instantes; se persistir, fale com o
                suporte.
              </p>
            )}
          </Cartao>
        )}
      </Estado>
    </section>
  );
}
